import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col, Alert } from "react-bootstrap";
import { createResource, updateResource } from "../Api/projectResourceService";
import { toast } from "react-toastify";
import { getTeamById } from "../Api/TeamMaster"; // Import team APIs
import { getUsersByIds } from "../Api/Auth";

const ResourceModal = ({
  show,
  onHide,
  resource,
  projectId,
  onResourceSaved,
  teamAssigned, // Add this prop to pass the team ID from ProjectView
}) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;
  const orgId = userDetails?.organisationId;

  const [formData, setFormData] = useState({
    resourceMember: "", // This will now store user ID
    resourceRole: "",
    resourceBillable: "Billable",
    resourceAllocaPercentage: "",
    resourceStartDate: "",
    resourceEndDate: "",
    resourceStatus: "Active",
    projectBelongTo: projectId,
    orgId: String(orgId),
    createdBy: String(userId),
    editedBy: String(userId),
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

  // Fetch team members when modal opens and teamAssigned is available
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!teamAssigned) {
        console.log("No team assigned to project");
        setTeamMembers([]);
        return;
      }

      setLoadingTeamMembers(true);
      try {
        console.log("Fetching team data for team ID:", teamAssigned);

        // Get team data
        const teamData = await getTeamById(teamAssigned);
        console.log("Team data:", teamData);

        if (
          teamData &&
          teamData.teamMembers &&
          teamData.teamMembers.length > 0
        ) {
          // Convert string IDs to numbers for the API
          const numericUserIds = teamData.teamMembers
            .map((id) => parseInt(id))
            .filter((id) => !isNaN(id));

          console.log("Fetching user details for IDs:", numericUserIds);

          if (numericUserIds.length > 0) {
            // Get user details for team members
            const response = await getUsersByIds(numericUserIds);
            console.log("User details response:", response);

            if (response && response.users) {
              setTeamMembers(response.users);
              console.log("Team members loaded:", response.users);
            } else {
              setTeamMembers([]);
              console.warn("No user details found in response");
            }
          } else {
            setTeamMembers([]);
            console.warn("No valid user IDs to fetch");
          }
        } else {
          setTeamMembers([]);
          console.log("No team members found in team data");
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
        toast.error("Error loading team members");
        setTeamMembers([]);
      } finally {
        setLoadingTeamMembers(false);
      }
    };

    if (show && teamAssigned) {
      fetchTeamMembers();
    }
  }, [show, teamAssigned]);

  useEffect(() => {
    if (resource) {
      // Editing existing resource - resourceMember already contains user ID
      const updatedFormData = {
        resourceMember: resource.resourceMember || "", // This should be user ID
        resourceRole: resource.resourceRole || "",
        resourceBillable: resource.resourceBillable || "Billable",
        resourceAllocaPercentage: resource.resourceAllocaPercentage || "",
        resourceStartDate: resource.resourceStartDate
          ? resource.resourceStartDate.split("T")[0]
          : "",
        resourceEndDate: resource.resourceEndDate
          ? resource.resourceEndDate.split("T")[0]
          : "",
        resourceStatus: resource.resourceStatus || "Active",
        projectBelongTo: projectId,
        orgId: String(orgId),
        createdBy: resource.createdBy || String(userId),
        editedBy: String(userId),
      };
      setFormData(updatedFormData);
      console.log("Editing Resource - Form Data:", updatedFormData);
    } else {
      // Creating new resource
      const newFormData = {
        resourceMember: "", // Will store user ID
        resourceRole: "",
        resourceBillable: "Billable",
        resourceAllocaPercentage: "",
        resourceStartDate: "",
        resourceEndDate: "",
        resourceStatus: "Active",
        projectBelongTo: projectId,
        orgId: String(orgId),
        createdBy: String(userId),
        editedBy: String(userId),
      };
      setFormData(newFormData);
      console.log("Creating New Resource - Form Data:", newFormData);
    }
    setError("");
  }, [resource, projectId, orgId, userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (resource) {
        console.log("Calling updateResource API...");
        const response = await updateResource(resource.resourceId, formData);
        console.log("Update API Response:", response);
        toast.success("Resource updated successfully");
      } else {
        console.log("Calling createResource API...");
        const response = await createResource(formData);
        console.log("Create API Response:", response);
        toast.success("Resource created successfully");
      }
      onResourceSaved();
    } catch (error) {
      console.error("Error saving resource:", error);
      setError(error.message || "Failed to save resource");
      toast.error("Failed to save resource");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get display name for selected user
  const getSelectedUserDisplayName = () => {
    if (!formData.resourceMember) return "";
    const selectedUser = teamMembers.find(
      (member) => member.userId === parseInt(formData.resourceMember)
    );
    return selectedUser
      ? `${selectedUser.username} (${selectedUser.email || "No email"})`
      : formData.resourceMember;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {resource ? "Edit Resource" : "Add New Resource"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Resource Member *</Form.Label>
                {loadingTeamMembers ? (
                  <Form.Control
                    type="text"
                    value="Loading team members..."
                    disabled
                  />
                ) : teamMembers.length > 0 ? (
                  <Form.Select
                    name="resourceMember"
                    value={formData.resourceMember}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a team member</option>
                    {teamMembers.map((member) => (
                      <option key={member.userId} value={member.userId}>
                        {member.username}{" "}
                        {member.email ? `(${member.email})` : ""}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    name="resourceMember"
                    value={formData.resourceMember}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter user ID (no team members found)"
                  />
                )}
                <Form.Text className="text-muted">
                  {teamMembers.length > 0
                    ? `Select from ${teamMembers.length} available team members`
                    : "No team members found. Please enter user ID manually."}
                </Form.Text>
                {formData.resourceMember && teamMembers.length > 0 && (
                  <Form.Text className="text-info">
                    Selected: {getSelectedUserDisplayName()}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Role *</Form.Label>
                <Form.Select
                  name="resourceRole"
                  value={formData.resourceRole}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a role</option>
                  <option value="Project Manager">Project Manager</option>
                  <option value="Developer">Developer</option>
                  <option value="Designer">Designer</option>
                  <option value="QA Engineer">QA Engineer</option>
                  <option value="Business Analyst">Business Analyst</option>
                  <option value="DevOps Engineer">DevOps Engineer</option>
                  <option value="Technical Lead">Technical Lead</option>
                  <option value="Solution Architect">Solution Architect</option>
                  <option value="Scrum Master">Scrum Master</option>
                  <option value="Product Owner">Product Owner</option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Billable Status</Form.Label>
                <Form.Select
                  name="resourceBillable"
                  value={formData.resourceBillable}
                  onChange={handleInputChange}
                >
                  <option value="Billable">Billable</option>
                  <option value="Non-Billable">Non-Billable</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Allocation Percentage *</Form.Label>
                <Form.Control
                  type="number"
                  name="resourceAllocaPercentage"
                  value={formData.resourceAllocaPercentage}
                  onChange={handleInputChange}
                  required
                  min="1"
                  max="100"
                  placeholder="Enter percentage (1-100)"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="resourceStartDate"
                  value={formData.resourceStartDate}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="resourceEndDate"
                  value={formData.resourceEndDate}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="resourceStatus"
                  value={formData.resourceStatus}
                  onChange={handleInputChange}
                >
                  <option value="Active">Active</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Debug Information */}
          {/* {process.env.NODE_ENV === "development" && (
            <Row>
              <Col>
                <div
                  style={{
                    background: "#f8f9fa",
                    padding: "10px",
                    borderRadius: "5px",
                    fontSize: "12px",
                    marginTop: "10px",
                  }}
                >
                  <strong>Debug Info:</strong>
                  <div>Project ID: {projectId}</div>
                  <div>Team Assigned: {teamAssigned || "Not assigned"}</div>
                  <div>Team Members Loaded: {teamMembers.length}</div>
                  <div>Selected User ID: {formData.resourceMember}</div>
                  <div>Org ID: {orgId}</div>
                  <div>User ID: {userId}</div>
                </div>
              </Col>
            </Row>
          )} */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading
              ? "Saving..."
              : resource
              ? "Update Resource"
              : "Create Resource"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ResourceModal;

// import React, { useState, useEffect } from "react";
// import { Modal, Form, Button, Row, Col, Alert } from "react-bootstrap";
// import { createResource, updateResource } from "../Api/projectResourceService";
// import { toast } from "react-toastify";
// import { getTeamById } from "../Api/TeamMaster"; // Import team APIs
// import { getUsersByIds } from "../Api/Auth";
// const ResourceModal = ({
//   show,
//   onHide,
//   resource,
//   projectId,
//   onResourceSaved,
//   teamAssigned, // Add this prop to pass the team ID from ProjectView
// }) => {
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId;
//   const orgId = userDetails?.organisationId;

//   const [formData, setFormData] = useState({
//     resourceMember: "",
//     resourceRole: "",
//     resourceBillable: "Billable",
//     resourceAllocaPercentage: "",
//     resourceStartDate: "",
//     resourceEndDate: "",
//     resourceStatus: "Active",
//     projectBelongTo: projectId,
//     orgId: String(orgId),
//     createdBy: String(userId),
//     editedBy: String(userId),
//   });

//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [teamMembers, setTeamMembers] = useState([]);
//   const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

//   // Fetch team members when modal opens and teamAssigned is available
//   useEffect(() => {
//     const fetchTeamMembers = async () => {
//       if (!teamAssigned) {
//         console.log("No team assigned to project");
//         setTeamMembers([]);
//         return;
//       }

//       setLoadingTeamMembers(true);
//       try {
//         console.log("Fetching team data for team ID:", teamAssigned);

//         // Get team data
//         const teamData = await getTeamById(teamAssigned);
//         console.log("Team data:", teamData);

//         if (
//           teamData &&
//           teamData.teamMembers &&
//           teamData.teamMembers.length > 0
//         ) {
//           // Convert string IDs to numbers for the API
//           const numericUserIds = teamData.teamMembers
//             .map((id) => parseInt(id))
//             .filter((id) => !isNaN(id));

//           console.log("Fetching user details for IDs:", numericUserIds);

//           if (numericUserIds.length > 0) {
//             // Get user details for team members
//             const response = await getUsersByIds(numericUserIds);
//             console.log("User details response:", response);

//             if (response && response.users) {
//               setTeamMembers(response.users);
//               console.log("Team members loaded:", response.users);
//             } else {
//               setTeamMembers([]);
//               console.warn("No user details found in response");
//             }
//           } else {
//             setTeamMembers([]);
//             console.warn("No valid user IDs to fetch");
//           }
//         } else {
//           setTeamMembers([]);
//           console.log("No team members found in team data");
//         }
//       } catch (error) {
//         console.error("Error fetching team members:", error);
//         toast.error("Error loading team members");
//         setTeamMembers([]);
//       } finally {
//         setLoadingTeamMembers(false);
//       }
//     };

//     if (show && teamAssigned) {
//       fetchTeamMembers();
//     }
//   }, [show, teamAssigned]);

//   useEffect(() => {
//     if (resource) {
//       // Editing existing resource
//       const updatedFormData = {
//         resourceMember: resource.resourceMember || "",
//         resourceRole: resource.resourceRole || "",
//         resourceBillable: resource.resourceBillable || "Billable",
//         resourceAllocaPercentage: resource.resourceAllocaPercentage || "",
//         resourceStartDate: resource.resourceStartDate
//           ? resource.resourceStartDate.split("T")[0]
//           : "",
//         resourceEndDate: resource.resourceEndDate
//           ? resource.resourceEndDate.split("T")[0]
//           : "",
//         resourceStatus: resource.resourceStatus || "Active",
//         projectBelongTo: projectId,
//         orgId: String(orgId),
//         createdBy: resource.createdBy || String(userId),
//         editedBy: String(userId),
//       };
//       setFormData(updatedFormData);
//       console.log("Editing Resource - Form Data:", updatedFormData);
//     } else {
//       // Creating new resource
//       const newFormData = {
//         resourceMember: "",
//         resourceRole: "",
//         resourceBillable: "Billable",
//         resourceAllocaPercentage: "",
//         resourceStartDate: "",
//         resourceEndDate: "",
//         resourceStatus: "Active",
//         projectBelongTo: projectId,
//         orgId: String(orgId),
//         createdBy: String(userId),
//         editedBy: String(userId),
//       };
//       setFormData(newFormData);
//       console.log("Creating New Resource - Form Data:", newFormData);
//     }
//     setError("");
//   }, [resource, projectId, orgId, userId]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//     console.log(`Field changed - ${name}:`, value);
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setError("");

//     try {
//       if (resource) {
//         console.log("Calling updateResource API...");
//         const response = await updateResource(resource.resourceId, formData);
//         console.log("Update API Response:", response);
//         toast.success("Resource updated successfully");
//       } else {
//         console.log("Calling createResource API...");
//         const response = await createResource(formData);
//         console.log("Create API Response:", response);
//         toast.success("Resource created successfully");
//       }
//       onResourceSaved();
//     } catch (error) {
//       console.error("Error saving resource:", error);
//       setError(error.message || "Failed to save resource");
//       toast.error("Failed to save resource");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Modal show={show} onHide={onHide} size="lg">
//       <Modal.Header closeButton>
//         <Modal.Title>
//           {resource ? "Edit Resource" : "Add New Resource"}
//         </Modal.Title>
//       </Modal.Header>
//       <Form onSubmit={handleSubmit}>
//         <Modal.Body>
//           {error && <Alert variant="danger">{error}</Alert>}

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Resource Member *</Form.Label>
//                 {loadingTeamMembers ? (
//                   <Form.Control
//                     type="text"
//                     value="Loading team members..."
//                     disabled
//                   />
//                 ) : teamMembers.length > 0 ? (
//                   <Form.Select
//                     name="resourceMember"
//                     value={formData.resourceMember}
//                     onChange={handleInputChange}
//                     required
//                   >
//                     <option value="">Select a team member</option>
//                     {teamMembers.map((member) => (
//                       <option key={member.userId} value={member.username}>
//                         {member.username}{" "}
//                         {member.email ? `(${member.email})` : ""}
//                       </option>
//                     ))}
//                   </Form.Select>
//                 ) : (
//                   <Form.Control
//                     type="text"
//                     name="resourceMember"
//                     value={formData.resourceMember}
//                     onChange={handleInputChange}
//                     required
//                     placeholder="Enter member name (no team members found)"
//                   />
//                 )}
//                 <Form.Text className="text-muted">
//                   {teamMembers.length > 0
//                     ? `Select from ${teamMembers.length} available team members`
//                     : "No team members found. Please enter manually."}
//                 </Form.Text>
//               </Form.Group>
//             </Col>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Role *</Form.Label>
//                 <Form.Select
//                   name="resourceRole"
//                   value={formData.resourceRole}
//                   onChange={handleInputChange}
//                   required
//                 >
//                   <option value="">Select a role</option>
//                   <option value="Project Manager">Project Manager</option>
//                   <option value="Developer">Developer</option>
//                   <option value="Designer">Designer</option>
//                   <option value="QA Engineer">QA Engineer</option>
//                   <option value="Business Analyst">Business Analyst</option>
//                   <option value="DevOps Engineer">DevOps Engineer</option>
//                   <option value="Technical Lead">Technical Lead</option>
//                   <option value="Solution Architect">Solution Architect</option>
//                   <option value="Scrum Master">Scrum Master</option>
//                   <option value="Product Owner">Product Owner</option>
//                   <option value="Other">Other</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Billable Status</Form.Label>
//                 <Form.Select
//                   name="resourceBillable"
//                   value={formData.resourceBillable}
//                   onChange={handleInputChange}
//                 >
//                   <option value="Billable">Billable</option>
//                   <option value="Non-Billable">Non-Billable</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Allocation Percentage *</Form.Label>
//                 <Form.Control
//                   type="number"
//                   name="resourceAllocaPercentage"
//                   value={formData.resourceAllocaPercentage}
//                   onChange={handleInputChange}
//                   required
//                   min="1"
//                   max="100"
//                   placeholder="Enter percentage (1-100)"
//                 />
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Start Date *</Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="resourceStartDate"
//                   value={formData.resourceStartDate}
//                   onChange={handleInputChange}
//                   required
//                 />
//               </Form.Group>
//             </Col>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>End Date *</Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="resourceEndDate"
//                   value={formData.resourceEndDate}
//                   onChange={handleInputChange}
//                   required
//                 />
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Status</Form.Label>
//                 <Form.Select
//                   name="resourceStatus"
//                   value={formData.resourceStatus}
//                   onChange={handleInputChange}
//                 >
//                   <option value="Active">Active</option>
//                   <option value="In Progress">In Progress</option>
//                   <option value="Completed">Completed</option>
//                   <option value="On Hold">On Hold</option>
//                   <option value="Inactive">Inactive</option>
//                 </Form.Select>
//               </Form.Group>
//             </Col>
//           </Row>

//           {/* Debug Information */}
//           {/* {process.env.NODE_ENV === "development" && (
//             <Row>
//               <Col>
//                 <div
//                   style={{
//                     background: "#f8f9fa",
//                     padding: "10px",
//                     borderRadius: "5px",
//                     fontSize: "12px",
//                     marginTop: "10px",
//                   }}
//                 >
//                   <strong>Debug Info:</strong>
//                   <div>Project ID: {projectId}</div>
//                   <div>Team Assigned: {teamAssigned || "Not assigned"}</div>
//                   <div>Team Members Loaded: {teamMembers.length}</div>
//                   <div>Org ID: {orgId}</div>
//                   <div>User ID: {userId}</div>
//                 </div>
//               </Col>
//             </Row>
//           )} */}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={onHide} disabled={loading}>
//             Cancel
//           </Button>
//           <Button variant="primary" type="submit" disabled={loading}>
//             {loading
//               ? "Saving..."
//               : resource
//               ? "Update Resource"
//               : "Create Resource"}
//           </Button>
//         </Modal.Footer>
//       </Form>
//     </Modal>
//   );
// };

// export default ResourceModal;
